import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { List, X, HelpCircle } from 'lucide-react';
import FaqSection from '../components/FaqSection';

/* ================================================================ */
/* Table of contents — one entry per major section.                 */
/* ================================================================ */

type TocSection = { id: string; label: string; kind: 'part' | 'sub' | 'faq' };

const TOC: TocSection[] = [
  { id: 'part-1', label: 'Part I · The Fundamentals', kind: 'part' },
  { id: 'biology-blood', label: 'Raman in Biology & Blood', kind: 'sub' },
  { id: 'sers-limits', label: 'SERS & Classical Limits', kind: 'sub' },
  { id: 'part-2', label: 'Part II · Quantum Enhancement', kind: 'part' },
  { id: 'preciqube-approach', label: "PreciQube's Approach", kind: 'sub' },
  { id: 'part-3', label: 'Part III · Squeezed Light', kind: 'part' },
  { id: 'faq', label: 'Frequently Asked Questions', kind: 'faq' },
];

/* ================================================================ */
/* Lightweight presentation pieces                                  */
/* ================================================================ */

const fadeIn = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.4 },
};

/* A consolidated section: a numbered title and a body of grouped content. */
const Section: React.FC<{
  id: string;
  index: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, index, title, children }) => (
  <motion.section {...fadeIn} id={id} className="scroll-mt-28">
    <h2 className="flex items-baseline gap-3 text-2xl md:text-3xl font-bold text-[var(--text-main)] transition-theme leading-tight mb-6">
      <span className="text-[var(--accent-blue)] font-mono text-lg">{index}</span>
      {title}
    </h2>
    <div className="text-[var(--text-muted)] leading-relaxed transition-theme space-y-4 text-[15px] md:text-base">
      {children}
    </div>
  </motion.section>
);

/* A light sub-heading used to bind related topics inside one section. */
const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-base md:text-lg font-bold text-[var(--text-main)] transition-theme pt-4">
    {children}
  </h3>
);

/* Plain bulleted list. */
const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent-blue)] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/* Term/definition list, e.g. the healthcare advantages. */
const Defs: React.FC<{ items: { term: string; text: string }[] }> = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent-blue)] flex-shrink-0" />
        <span>
          <strong className="text-[var(--text-main)]">{item.term}.</strong> {item.text}
        </span>
      </li>
    ))}
  </ul>
);

/* Sparingly used emphasis for the single most important line in a section. */
const Quote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="border-l-2 border-[var(--accent-blue)] pl-5 py-1 text-lg font-semibold text-[var(--text-main)] transition-theme">
    {children}
  </p>
);

/* An illustrative figure with a caption. */
const Figure: React.FC<{ src: string; alt: string; caption?: string }> = ({ src, alt, caption }) => (
  <figure className="my-6">
    <div className="rounded-2xl overflow-hidden border border-[var(--card-border)] bg-[var(--card-bg)] transition-theme">
      <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
    </div>
    {caption && (
      <figcaption className="mt-3 text-sm text-[var(--text-muted)] italic text-center transition-theme">
        {caption}
      </figcaption>
    )}
  </figure>
);

const PartDivider: React.FC<{ id: string; eyebrow: string; title: string; blurb: string }> = ({
  id,
  eyebrow,
  title,
  blurb,
}) => (
  <motion.div {...fadeIn} id={id} className="scroll-mt-28 pt-4">
    <div className="inline-block bg-[var(--accent-blue)] text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-md mb-4">
      {eyebrow}
    </div>
    <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[var(--text-main)] transition-theme leading-tight">
      {title}
    </h2>
    <p className="text-[var(--text-muted)] leading-relaxed transition-theme">{blurb}</p>
  </motion.div>
);

/* ================================================================ */
/* Scroll-spy for the contents sidebar                              */
/* ================================================================ */

const useScrollSpy = (ids: string[]) => {
  const [activeId, setActiveId] = useState<string>(ids[0] ?? '');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-120px 0px -65% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return activeId;
};

const TocList: React.FC<{ activeId: string; onNavigate?: () => void }> = ({
  activeId,
  onNavigate,
}) => {
  // HashRouter owns the URL hash, so we scroll manually instead of href="#id".
  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    onNavigate?.();
  };

  return (
    <nav className="space-y-1">
      {TOC.map((s) => {
        const isActive = activeId === s.id;

        // FAQ: a distinct pill that stands apart from the science links.
        if (s.kind === 'faq') {
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={scrollTo(s.id)}
              className={`mt-4 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors border ${
                isActive
                  ? 'bg-[var(--accent-blue)] border-[var(--accent-blue)] text-white'
                  : 'border-[var(--card-border)] text-[var(--text-main)] hover:border-[var(--accent-blue)] hover:text-[var(--accent-blue)]'
              }`}
            >
              <HelpCircle className="w-4 h-4 flex-shrink-0" />
              FAQ
            </a>
          );
        }

        // Part headers: bold, uppercase, no left rule — they label a group.
        if (s.kind === 'part') {
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={scrollTo(s.id)}
              className={`block pt-4 pb-1 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors ${
                isActive
                  ? 'text-[var(--accent-blue)]'
                  : 'text-[var(--text-main)] hover:text-[var(--accent-blue)]'
              }`}
            >
              {s.label}
            </a>
          );
        }

        // Sub-sections: indented links with the active left rule.
        return (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={scrollTo(s.id)}
            className={`block ml-1 pl-4 border-l-2 py-1.5 text-sm transition-colors ${
              isActive
                ? 'border-[var(--accent-blue)] text-[var(--accent-blue)] font-semibold'
                : 'border-[var(--card-border)] text-[var(--text-muted)] hover:text-[var(--text-main)] hover:border-[var(--text-muted)]'
            }`}
          >
            {s.label}
          </a>
        );
      })}
    </nav>
  );
};

/* ================================================================ */
/* Page                                                             */
/* ================================================================ */

const Science: React.FC = () => {
  const allIds = useRef(TOC.map((s) => s.id)).current;
  const activeId = useScrollSpy(allIds);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  return (
    <div className="w-full px-6 md:px-12 lg:px-16 pt-32">
      {/* Hero */}
      <header className="max-w-3xl mb-12 pb-12 border-b border-[var(--card-border)]">
        <span className="text-[var(--accent-blue)] font-bold text-sm uppercase tracking-[0.2em] transition-theme">
          The Science
        </span>
        <h1 className="text-4xl md:text-6xl font-bold mt-4 mb-6 text-[var(--text-main)] transition-theme leading-[1.05]">
          Reading the molecular fingerprint of biology
        </h1>
        <p className="text-[var(--text-muted)] text-lg leading-relaxed transition-theme">
          A complete look at the science behind PreciQube from a century-old optical technique to
          the quantum frontier of molecular sensing. Use the contents to jump to any section.
        </p>
      </header>

      {/* Mobile TOC toggle */}
      <div className="lg:hidden mb-8">
        <button
          onClick={() => setMobileTocOpen(true)}
          className="flex items-center gap-2 glass-card rounded-xl px-4 py-3 text-sm font-semibold text-[var(--text-main)] transition-theme"
        >
          <List className="w-4 h-4 text-[var(--accent-blue)]" />
          Contents
        </button>
      </div>

      <div className="flex gap-12 xl:gap-20">
        {/* Sticky sidebar (desktop) */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 pb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent-blue)] mb-4 transition-theme">
              Contents
            </p>
            <TocList activeId={activeId} />
          </div>
        </aside>

        {/* Main article */}
        <article className="flex-1 min-w-0 max-w-6xl space-y-14">
          {/* ============== PART I ============== */}
          <PartDivider
            id="part-1"
            eyebrow="Part I"
            title="Raman Spectroscopy: Reading the Molecular Fingerprint of Biology"
            blurb="How light can reveal the molecular makeup of biological samples and why that matters for the future of diagnostics."
          />

          <Section id="understanding-raman" index="01" title="Understanding Raman Spectroscopy">
            <p>
              Raman spectroscopy is a powerful optical technique that uses light to study the
              molecular composition of materials.
            </p>
            <p>
              Every molecule vibrates in a unique way. These vibrations create distinctive patterns,
              much like fingerprints. By analyzing how light interacts with a sample, Raman
              spectroscopy can reveal information about its molecular makeup without requiring dyes,
              labels, or chemical reagents. For nearly a century, Raman spectroscopy has been used
              across chemistry, materials science, pharmaceuticals, and increasingly in medicine.
            </p>

            <Sub>How does it work?</Sub>
            <p>
              A laser beam is directed onto a sample. Most of the light simply bounces back
              unchanged. However, a very small fraction of the light interacts with molecules inside
              the sample and carries information about their vibrations. By analyzing these subtle
              changes in light, researchers can obtain a detailed molecular fingerprint of the
              sample.
            </p>

            <Sub>Why is this important?</Sub>
            <p>
              Traditional diagnostic methods often focus on measuring one specific biomarker. Raman
              spectroscopy offers a different approach. Instead of looking for a single molecule, it
              captures information from many proteins, lipids, metabolites, and other biomolecules
              simultaneously. This creates a rich biochemical picture that may reveal subtle changes
              associated with disease.
            </p>
            <Figure
              src="/images/Raman-scattering.jpeg"
              alt="Diagram of Raman scattering: an incident laser interacts with a molecule, producing Rayleigh, Stokes, and anti-Stokes scattered light that together form a molecular fingerprint."
              caption="Raman scattering: light interacts with molecular vibrations, and the shifts in the scattered light form a unique molecular fingerprint."
            />
          </Section>

          <Section id="built-for-healthcare" index="02" title="Built for Healthcare">
            <p>Several properties make Raman spectroscopy attractive for healthcare:</p>
            <Defs
              items={[
                { term: 'Label-Free', text: 'No fluorescent dyes or chemical labels are required.' },
                { term: 'Non-Destructive', text: 'Samples remain intact after measurement.' },
                {
                  term: 'Rich Molecular Information',
                  text:
                    'Proteins, lipids, metabolites, and other biomolecules contribute to the measured spectrum.',
                },
                {
                  term: 'Minimal Sample Preparation',
                  text: 'Measurements can often be performed with very little processing.',
                },
                { term: 'Rapid Measurements', text: 'Results can be obtained in seconds or minutes.' },
              ]}
            />

            <Sub>A technology used across many fields</Sub>
            <p>Raman spectroscopy is widely used in:</p>
            <Bullets
              items={[
                'Pharmaceutical quality control',
                'Materials science',
                'Semiconductor manufacturing',
                'Environmental monitoring',
                'Food safety',
                'Chemical analysis',
                'Biomedical research',
              ]}
            />
            <p>
              Its versatility has made it one of the most important analytical tools in modern
              science.
            </p>
          </Section>

          <Section id="biology-blood" index="03" title="From Biology to Blood">
            <p>
              Biological samples such as blood, serum, saliva, tissues, and cells contain thousands
              of molecules. Together, these molecules create complex spectral patterns that reflect
              the biochemical state of the sample. Researchers around the world are investigating
              whether these molecular fingerprints can help identify changes associated with disease.
            </p>

            <Sub>Why blood?</Sub>
            <p>
              Blood carries information from throughout the body. Proteins, lipids, metabolites,
              nucleic acids, and extracellular vesicles present in blood provide a window into overall
              health. Because blood collection is minimally invasive and easily repeatable,
              blood-based approaches are particularly attractive for early detection and monitoring.
            </p>

            <Sub>The challenge</Sub>
            <p>
              One of the biggest challenges of Raman spectroscopy is that the signals are extremely
              weak. In biological samples, these weak signals can be obscured by background
              fluorescence and measurement noise. Overcoming these limitations has been a major focus
              of scientific research for decades.
            </p>
          </Section>

          <Section id="sers-limits" index="04" title="SERS, Classical Limits & the Road Ahead">
            <Sub>Surface-Enhanced Raman Spectroscopy (SERS)</Sub>
            <p>
              Surface-Enhanced Raman Spectroscopy, or SERS, uses specially engineered metallic
              nanostructures to amplify Raman signals. This enhancement enables researchers to study
              molecules that would otherwise be difficult to detect. SERS has opened new possibilities
              in chemical sensing, biomedical research, and molecular diagnostics.
            </p>

            <Sub>Looking beyond classical limits</Sub>
            <p>
              Modern Raman systems have become extraordinarily sensitive. As conventional technologies
              continue to improve, researchers are increasingly exploring new approaches inspired by
              quantum optics to further enhance measurement sensitivity. These emerging techniques
              seek to push beyond the fundamental noise limits of classical optical measurements and
              may enable the next generation of molecular sensing technologies.
            </p>

            <Sub>The future</Sub>
            <p>Researchers worldwide are working to combine advances in:</p>
            <Bullets
              items={[
                'Nanotechnology',
                'Artificial intelligence',
                'Machine learning',
                'Photonics',
                'Quantum sensing',
              ]}
            />
            <p>
              These developments may help transform Raman spectroscopy into an increasingly powerful
              tool for healthcare and precision medicine.
            </p>

            <Sub>A century of scientific innovation</Sub>
            <p>
              Discovered by Sir C. V. Raman in 1928, Raman spectroscopy has evolved into one of the
              most versatile analytical techniques in modern science. Today, it continues to inspire
              new technologies aimed at understanding matter, biology, and disease at the molecular
              level.
            </p>
          </Section>

          {/* ============== PART II ============== */}
          <PartDivider
            id="part-2"
            eyebrow="Part II"
            title="SERS and the Next Frontier of Quantum-Enhanced Biosensing"
            blurb="A deeper look at amplifying weak signals, the tradeoffs involved, and how quantum optics opens a new path beyond classical limits."
          />

          <Section id="amplifying-signal" index="05" title="Amplifying the Signal with SERS">
            <p>
              One of the fundamental challenges of Raman spectroscopy is that Raman signals are
              inherently weak. Only a tiny fraction of photons interacting with a sample carry useful
              molecular information, making sensitive measurements difficult. Surface-Enhanced Raman
              Spectroscopy (SERS) was developed to overcome this limitation.
            </p>
            <p>
              By introducing carefully engineered metallic nanostructures, typically made of gold or
              silver, local electromagnetic fields can be dramatically enhanced. Molecules located
              near these structures experience much stronger optical fields, resulting in Raman
              signals that are many orders of magnitude larger than conventional Raman spectroscopy.
              Over the past several decades, SERS has emerged as one of the most powerful approaches
              for ultrasensitive molecular detection.
            </p>
            <Figure
              src="/images/Sers.jpeg"
              alt="Comparison of conventional Raman and Surface-Enhanced Raman Spectroscopy (SERS), showing how metallic nanostructures create hotspots that amplify the molecular signal by orders of magnitude."
              caption="From Raman to SERS: metallic nanostructures create electromagnetic hotspots that amplify weak molecular signals by many orders of magnitude."
            />

            <Sub>The sensitivity–reproducibility tradeoff</Sub>
            <p>
              While SERS provides remarkable signal enhancement, its greatest strength also gives rise
              to one of its most significant challenges. Signal enhancement originates from nanoscale
              regions known as hotspots. These hotspots are highly sensitive to the exact geometry,
              size, and spacing of the nanostructures. As a consequence, the strongest signal
              enhancement is often associated with highly localized and difficult-to-control hotspots.
              This creates a fundamental engineering tradeoff:
            </p>
            <Quote>Greater enhancement often comes at the expense of reproducibility.</Quote>
            <p>
              Random nanoparticle aggregates can produce enormous signal amplification, but their
              response varies from sample to sample and even from point to point on the same
              substrate. Conversely, highly reproducible substrates typically sacrifice some
              enhancement in favor of uniformity and reliability. For clinical applications,
              reproducibility is just as important as sensitivity.
            </p>

            <Sub>Beyond maximum enhancement</Sub>
            <p>
              Historically, much of the SERS community has focused on maximizing enhancement factors.
              However, for real-world diagnostic applications, maximizing enhancement alone may not be
              the optimal strategy. A highly reproducible and well-controlled substrate with moderate
              enhancement may ultimately provide more reliable measurements than one with extreme but
              unpredictable hotspots. This perspective shifts the emphasis from pursuing the largest
              possible enhancement factors toward achieving a balance between sensitivity, stability,
              and reproducibility.
            </p>
          </Section>

          <Section id="lowering-noise" index="06" title="Lowering the Noise with Quantum Optics">
            <Sub>The limits of classical optical measurements</Sub>
            <p>
              Modern Raman instruments have benefited from decades of improvements in lasers,
              detectors, spectrometers, and signal processing. These advances have substantially
              reduced technical noise and brought measurements increasingly close to their fundamental
              shot-noise limits. Once this limit is reached, further improvements become difficult.
              Increasing laser power can improve sensitivity, but excessive optical power may introduce
              heating, photodamage, or unwanted background effects. Longer acquisition times can also
              improve signal-to-noise ratio, but reduce throughput and practicality. At this stage,
              entirely new approaches become attractive.
            </p>

            <Sub>Quantum-enhanced Raman spectroscopy</Sub>
            <p>
              Quantum optics offers a fundamentally different route to improving measurement
              sensitivity. Instead of relying solely on stronger signal enhancement, quantum states of
              light can reduce the intrinsic noise associated with optical measurements.
            </p>
            <Quote>
              Squeezed light can push stimulated Raman measurements below the classical shot-noise
              limit, providing genuine improvements in signal-to-noise ratio.
            </Quote>
            <p>
              These demonstrations have been achieved by multiple independent groups in polymer
              samples and even living biological cells, establishing quantum-enhanced Raman
              spectroscopy as a real and experimentally validated field.
            </p>

            <Sub>A complementary approach</Sub>
            <p>
              Quantum-enhanced Raman spectroscopy does not replace SERS. Instead, the two approaches
              address different aspects of the measurement problem. <strong className="text-[var(--text-main)]">SERS amplifies the signal.
              Quantum optics reduces the noise.</strong> Together, these strategies may offer a path
              toward improved sensitivity without relying exclusively on extreme hotspot enhancement.
            </p>
          </Section>

          <Section id="preciqube-approach" index="07" title="Toward a Reproducible Platform">
            <p>This suggests a different philosophy for biosensing. Rather than pursuing the largest possible enhancement factors, it may be advantageous to design substrates that prioritize:</p>
            <Bullets
              items={['Uniformity', 'Stability', 'Reproducibility', 'Controlled electromagnetic enhancement']}
            />
            <p>
              Moderate enhancement combined with quantum-noise reduction could potentially achieve
              similar or superior overall sensitivity while improving measurement reliability.
            </p>

            <Sub>A possible future direction</Sub>
            <p>PreciQube is exploring the hypothesis that combining:</p>
            <Bullets
              items={[
                'Reproducible nanostructured substrates,',
                'Stimulated Raman spectroscopy,',
                'Advanced signal processing,',
                'And quantum-enhanced optical measurements,',
              ]}
            />
            <p>
              may provide a path toward more sensitive and reproducible molecular fingerprinting. This
              concept remains an active area of research and development, and significant scientific
              and engineering challenges remain.
            </p>

            <Sub>Looking forward</Sub>
            <p>
              The next generation of biosensing technologies may not arise from maximizing any single
              component of the system. Instead, progress is likely to emerge from the convergence of:
            </p>
            <Bullets
              items={[
                'Nanotechnology,',
                'Photonics,',
                'Machine learning,',
                'Surface engineering,',
                'And quantum optics.',
              ]}
            />
            <p>
              Together, these advances may enable new approaches to molecular diagnostics that balance
              sensitivity, robustness, and scalability.
            </p>
          </Section>

          {/* ============== PART III ============== */}
          <PartDivider
            id="part-3"
            eyebrow="Part III"
            title="Squeezed Light: Engineering Quantum Noise"
            blurb="The physics behind quantum-enhanced measurement how light's own uncertainty can be reshaped to measure beyond the limits of ordinary lasers."
          />

          <Section id="engineering-quantum-noise" index="08" title="Engineering Quantum Noise">
            <p>
              One of the most remarkable features of quantum mechanics is that uncertainty is not
              merely a consequence of imperfect instruments, it is an intrinsic property of nature.
              Even a perfectly stable laser operating in complete isolation exhibits fluctuations
              arising from the quantum nature of light itself. These fluctuations ultimately limit the
              precision with which measurements can be performed. Squeezed states of light provide a
              way to engineer these quantum fluctuations, enabling measurements beyond the limits
              imposed by ordinary laser light.
            </p>

            <Sub>Light as a wave</Sub>
            <p>
              Light is often described as a stream of photons, but it can also be viewed as an
              electromagnetic wave. A monochromatic optical field can be characterized by two
              independent quantities: <strong className="text-[var(--text-main)]">amplitude</strong>{' '}
              and <strong className="text-[var(--text-main)]">phase</strong>. The amplitude determines
              the intensity of the light, while the phase describes the relative position of the wave
              oscillation. These two variables completely describe a coherent optical field.
            </p>

            <Sub>Quantum fluctuations</Sub>
            <p>
              In classical physics, one could imagine both amplitude and phase being known with
              arbitrary precision. Quantum mechanics, however, places a fundamental constraint on how
              accurately these quantities can be specified simultaneously. The amplitude and phase
              quadratures of light obey an uncertainty relation analogous to Heisenberg's uncertainty
              principle:
            </p>
            <p className="font-mono text-center text-[var(--text-main)] text-lg py-2">
              ΔX₁ ΔX₂ ≥ 1/4
            </p>
            <p>where:</p>
            <Bullets
              items={[
                <>
                  <strong className="text-[var(--text-main)]">X₁</strong> represents one field
                  quadrature.
                </>,
                <>
                  <strong className="text-[var(--text-main)]">X₂</strong> represents the orthogonal
                  quadrature.
                </>,
              ]}
            />
            <p>
              These quadratures are closely related to amplitude and phase fluctuations. Just as
              position and momentum cannot both be known perfectly, amplitude and phase noise cannot
              both be simultaneously reduced to zero.
            </p>

            <Sub>Vacuum fluctuations</Sub>
            <p>
              Even in the absence of photons, quantum fluctuations remain. These fluctuations are
              known as vacuum fluctuations. For ordinary laser light, known as a coherent state, these
              fluctuations are distributed equally between the two quadratures. Graphically, the
              uncertainty appears as a circular region in phase space. This circular uncertainty
              region defines the Standard Quantum Limit.
            </p>
          </Section>

          <Section id="squeezing-noise" index="09" title="Squeezing the Noise">
            <p>
              Quantum mechanics does not allow the total uncertainty to disappear. However, it does
              allow the distribution of uncertainty to be manipulated. By reducing uncertainty in one
              quadrature, the uncertainty in the conjugate quadrature must increase. This process is
              known as squeezing. Instead of a circle, the uncertainty region becomes an ellipse:
              smaller in one direction, larger in the orthogonal direction. Importantly, the total
              uncertainty remains consistent with Heisenberg's uncertainty principle.
            </p>
            <Quote>
              Quantum mechanics forbids eliminating uncertainty altogether, but it allows us to decide
              where that uncertainty resides.
            </Quote>
            <Figure
              src="/images/brighsqStates.png"
              alt="Phase-space and quadrature plots of coherent versus squeezed states of light, showing how the circular uncertainty of a coherent state is reshaped into an ellipse — reduced noise in one quadrature at the expense of the other."
              caption="Squeezing reshapes quantum uncertainty: the circular noise of ordinary (coherent) light becomes an ellipse — reduced in one quadrature at the cost of the other."
            />

            <Sub>Amplitude-squeezed light</Sub>
            <p>
              In amplitude squeezing, intensity fluctuations are reduced below the shot-noise limit.
              As a consequence, phase fluctuations increase. Amplitude squeezing is particularly
              attractive for spectroscopy and sensing because many measurements depend directly on
              intensity fluctuations. Reducing amplitude noise improves the signal-to-noise ratio
              without increasing optical power.
            </p>

            <Sub>Phase-squeezed light</Sub>
            <p>
              In phase squeezing, phase fluctuations are reduced below the standard quantum limit.
              This comes at the expense of increased amplitude noise. Phase-squeezed states are
              particularly valuable in interferometry and gravitational-wave detection. The LIGO
              observatory uses squeezed states of light to improve sensitivity beyond what
              conventional lasers can achieve.
            </p>

            <Sub>Generating squeezed light</Sub>
            <p>Squeezed states are produced through nonlinear optical interactions. Common approaches include:</p>
            <Defs
              items={[
                {
                  term: 'Optical Parametric Oscillators (OPOs)',
                  text:
                    'A nonlinear crystal placed inside an optical cavity converts pump photons into correlated photon pairs. These correlations redistribute quantum fluctuations and produce squeezed vacuum states.',
                },
                {
                  term: 'Four-Wave Mixing',
                  text:
                    'Nonlinear interactions in optical fibers or atomic vapors generate correlated photons that exhibit reduced noise.',
                },
                {
                  term: 'Integrated Photonic Devices',
                  text:
                    'Advances in silicon photonics and nonlinear waveguides are enabling compact and scalable squeezed-light sources.',
                },
              ]}
            />
          </Section>

          <Section id="why-squeezing-matters" index="10" title="Why It Matters">
            <p>Many modern optical systems have already eliminated technical noise arising from:</p>
            <Bullets
              items={['Electronics', 'Laser instability', 'Thermal fluctuations', 'Mechanical vibrations']}
            />
            <p>
              As these systems approach the shot-noise limit, quantum fluctuations become the dominant
              source of uncertainty. At this stage, improvements through conventional engineering
              become increasingly difficult. Squeezed states provide an entirely new degree of
              freedom. Rather than increasing signal strength, they enable the noise itself to be
              engineered.
            </p>

            <Sub>From signal engineering to noise engineering</Sub>
            <p>
              Historically, advances in sensing focused on increasing signal strength. Quantum optics
              introduces a complementary philosophy. Instead of asking <em>how can we make the signal
              larger?</em> we can ask <em>how can we make the noise smaller?</em> This shift from
              signal engineering to noise engineering lies at the heart of quantum sensing.
            </p>

            <Sub>Applications</Sub>
            <p>Squeezed states have already found applications in:</p>
            <Bullets
              items={[
                'Gravitational-wave detectors',
                'Atomic clocks',
                'Magnetometers',
                'Quantum communications',
                'Microscopy',
                'Spectroscopy',
              ]}
            />
            <p>
              Recent experiments have demonstrated sub-shot-noise Raman spectroscopy and
              quantum-enhanced nonlinear microscopy, establishing squeezed-light spectroscopy as a
              genuine and rapidly advancing field.
            </p>

            <Sub>Looking ahead</Sub>
            <p>
              Squeezed light represents one of the most elegant consequences of quantum mechanics. It
              does not violate Heisenberg's uncertainty principle. Instead, it exploits it. By
              intelligently redistributing quantum uncertainty, squeezed states enable measurements
              that were once thought impossible. As classical technologies approach their fundamental
              limits, the ability to engineer quantum noise itself may become one of the defining
              capabilities of next-generation sensing systems.
            </p>
          </Section>
        </article>
      </div>

      {/* FAQ — full-width dark band, visually distinct from the article above */}
      <FaqSection breakout="-mx-6 md:-mx-12 lg:-mx-16" />

      {/* Mobile TOC drawer */}
      {mobileTocOpen && (
        <div className="lg:hidden fixed inset-0 z-[200] flex">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileTocOpen(false)}
          />
          <div className="relative ml-auto w-80 max-w-[85vw] h-full bg-[var(--bg-main)] shadow-2xl p-6 overflow-y-auto transition-theme">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent-blue)]">
                Contents
              </p>
              <button onClick={() => setMobileTocOpen(false)} aria-label="Close contents">
                <X className="w-5 h-5 text-[var(--text-main)]" />
              </button>
            </div>
            <TocList activeId={activeId} onNavigate={() => setMobileTocOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Science;
